import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface Me {
  name: string;
  login: string;
  id: string;
  avatarUrl?: string;
}

interface Result {
  data: Me | undefined;
  error: Error | null;
  loading: boolean;
}

interface APIMe {
  name: string;
  login: string;
  id: string;
  avatar_url?: string;
}

const getQueryKey = (): string[] => {
  return ['me'];
};

export const useInvalidateMe = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: getQueryKey(),
    });
  };
};

export class ForbiddenError extends Error {
  constructor() {
    super('You are not logged in');
    this.name = 'ForbiddenError';
  }
}

const hostname = 'circleci.com';

export const useMe = (): Result => {
  const { data, error, isLoading } = useQuery<Me | undefined, Error>(
    getQueryKey(),
    async (): Promise<Me | undefined> => {
      return fetch(`https://${hostname}/api/v2/me`, {
        credentials: 'include',
      }).then(async (response) => {
        if (
          !response.ok &&
          (response.status === 403 || response.status === 401)
        ) {
          return null;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch me: ' + response.status);
        }

        const apiResponse: APIMe = await response.json();
        const res: Me = {
          id: apiResponse.id,
          name: apiResponse.name,
          login: apiResponse.login,
          avatarUrl: apiResponse.avatar_url,
        };
        return res;
      });
    },
    {
      retry: 0,
    },
  );
  return {
    data: data as never,
    error,
    loading: isLoading,
  };
};
